// Conversation-side read model for character openings ("intros").
//
// A character may publish an ordered `ConversationIntro[]` (see
// `@/types/domain`). This module normalizes that collection (or a legacy
// single `initial_message`) into the list a new conversation seeds as
// alternative variants of its opening assistant message.
//
// Shared with the creator draft model (`@/lib/characterOpenings`): the blank
// predicate and the legacy-derivation id scheme must stay identical on both
// sides, and the derivation id scheme must also stay reproducible by the
// Supabase migration backfill (plain string concatenation: `legacy-<id>`).

import type { ConversationIntro } from '@/types/domain'

/** Blank means content with no non-whitespace characters. Shared everywhere. */
export function isBlankIntroContent(content: string | null | undefined): boolean {
  return !content || content.trim().length === 0
}

/**
 * The deterministic id a legacy `initial_message` is derived into. Kept
 * simple and stable so a plpgsql backfill can reproduce it as string
 * concatenation: `'legacy-' || character_id`.
 */
export function legacyIntroId(characterId: string | number): string {
  return `legacy-${characterId}`
}

/**
 * Derives the one-item legacy collection a character's `initial_message`
 * represents. Returns an empty array when the message is blank/absent —
 * zero openings is a valid state, so a blank legacy message contributes
 * nothing rather than a blank entry.
 */
export function deriveLegacyIntro(
  characterId: string | number,
  initialMessage: string | null | undefined,
): ConversationIntro[] {
  if (isBlankIntroContent(initialMessage)) return []
  return [{ id: legacyIntroId(characterId), content: initialMessage as string }]
}

/**
 * Normalizes a character's intro collection for conversation consumption.
 *
 * - `introMessages` present (including an explicitly empty array) is the
 *   source of truth; it is NOT overridden by `initialMessage`.
 * - `introMessages` null/undefined means a legacy writer only supplied
 *   `initialMessage`; derive a single legacy entry from it instead.
 * - Blank entries are removed — conversation seeding never presents a blank
 *   opening.
 * - Creator-defined order is preserved.
 * - Returns `[]` when neither source has usable content.
 */
export function normalizeConversationIntros(
  introMessages: ConversationIntro[] | null | undefined,
  initialMessage: string | null | undefined,
  characterId: string | number,
): ConversationIntro[] {
  const source = introMessages ?? deriveLegacyIntro(characterId, initialMessage)

  return source
    .filter((entry) => !isBlankIntroContent(entry?.content))
    .map((entry) => ({ id: String(entry.id), content: entry.content }))
}
